// 🧮 MELD A PARTIR DO EXAME ANONIMIZADO — B334 (AI_149)
// ─────────────────────────────────────────────────────────────
// Por que existe: o critério de irressecabilidade "MELD > 10" (laudo de Situação
// Especial por CHC) só reconhecia o MELD DIGITADO no campo "MELD-Na da inscrição"
// (`inscricaoTx.meldNa` → `scores.meldNa`). Se o médico não tivesse digitado, o
// critério não disparava — mesmo com o app mostrando o MELD calculado AO VIVO em
// todas as outras telas (B248/B248-FU) e o exame tendo os 5 componentes.
// Achado ao vivo por Leonardo (28/07): paciente com MELD 12 e o laudo dizendo que
// nenhum critério foi identificado.
//
// ⚖️ REGRA DE OURO DESTE MÓDULO: o número que sai daqui tem de ser EXATAMENTE o
// que o médico vê na tela. Por isso a precedência é a MESMA das telas
// (`meldVivoDoExame` na Avaliação Clínica e a planilha de Exames Lab):
// **calcula dos 5 componentes primeiro; o `meld` gravado é só fallback.**
// (Achado da revisão adversarial: gravar ≠ calcular — há exames em produção com
// `meld` gravado por uma versão ANTERIOR da fórmula, e o gravado também não
// acompanha correção de sexo no cadastro. Preferir o gravado faria o laudo citar
// um número diferente do da tela.)
//
// ⚠️ Escopo: usado APENAS no laudo SE-CHC (front + valor injetado no payload
// daquele pedido). Não altera a análise geral, o chat nem os demais laudos.
// ─────────────────────────────────────────────────────────────

import { calcularMELD } from "../../utils/meld";

/** Exame no formato anonimizado (`anonimizarDados.montarValoresLab`). */
export interface ExameAnonimizadoMeld {
  data?: string;
  dataColeta?: string;
  coletaNumero?: number | string;
  valores?: Record<string, unknown>;
}

// ⚠️ B337 (AI_149) — FAIXAS DE PLAUSIBILIDADE dos componentes do MELD.
// Não são faixas de normalidade (doente foge da normalidade o tempo todo): são
// limites de VEROSSIMILHANÇA, para pegar erro de DIGITAÇÃO. Caso real (28/07):
// albumina digitada como "0,1 g/dL" — incompatível com a vida — entrou no MELD
// 3.0, que trava a albumina no piso 1,5 e por isso INFLOU o score (~+3 pontos),
// sem ninguém perceber. Num laudo de Situação Especial esse número vira critério
// regulatório, então vale avisar antes.
const PLAUSIVEL: Record<string, [number, number, string]> = {
  Creatinina: [0.1, 20, "creatinina (mg/dL)"],
  BT: [0.05, 60, "bilirrubina total (mg/dL)"],
  INR: [0.5, 15, "INR"],
  Sodio: [100, 180, "sódio (mEq/L)"],
  Albumina: [1.0, 6.0, "albumina (g/dL)"],
};

export interface MeldDeExame {
  /** Score MELD 3.0. */
  valor: number;
  /** Data do exame de onde veio (como está no dado: ISO ou BR). */
  data: string;
  /** `calculado` = derivado dos 5 componentes (padrão); `gravado` = campo MELD do exame. */
  origem: "gravado" | "calculado";
  /**
   * Componentes com valor IMPLAUSÍVEL (provável erro de digitação) usados neste
   * cálculo. Vazio = nada suspeito. Não impede o cálculo — apenas sinaliza.
   */
  componentesSuspeitos: string[];
  /**
   * O exame ainda está DENTRO da própria validade (NT 01/2011: faixa do MELD)?
   * Falso = o valor serve de pista, mas NÃO pode ser afirmado como critério sem
   * conferência humana — mesma disciplina que o bloco já aplica ao sódio.
   */
  dentroDaValidade: boolean;
}

function num(x: unknown): number | null {
  if (x === undefined || x === null || x === "") return null;
  const n = parseFloat(String(x).replace(",", "."));
  return isNaN(n) ? null : n;
}

/** Validade do exame em dias pela faixa do MELD (NT 01/2011-SP). */
export function validadeDiasMeld(meld: number): number {
  if (meld <= 10) return 365;
  if (meld <= 18) return 90;
  if (meld <= 25) return 30;
  return 7;
}

/**
 * MELD de UM exame anonimizado. Calcula dos 5 componentes (Creatinina/BT/INR/
 * Sodio/Albumina); só se faltar componente usa o `MELD` gravado. `null` se nada
 * for determinável. Espelha `meldVivoDoExame` da Avaliação Clínica sobre os
 * RÓTULOS do exame anonimizado.
 */
export function meldDoExameAnonimizado(
  exame: ExameAnonimizadoMeld,
  sexoFeminino: boolean
): Omit<MeldDeExame, "dentroDaValidade"> | null {
  /** Componentes fora da faixa de verossimilhança (provável erro de digitação). */
  const suspeitos = (v: Record<string, unknown>): string[] => {
    const fora: string[] = [];
    for (const [chave, [min, max, rotulo]] of Object.entries(PLAUSIVEL)) {
      const n = num(v[chave]);
      if (n !== null && (n < min || n > max)) fora.push(rotulo);
    }
    return fora;
  };
  const v = (exame?.valores || {}) as Record<string, unknown>;
  const data = String(exame?.data || exame?.dataColeta || "");

  const creatinina = num(v.Creatinina);
  const bilirrubina = num(v.BT);
  const inr = num(v.INR);
  const sodio = num(v.Sodio);
  const albumina = num(v.Albumina);
  if (
    creatinina !== null &&
    bilirrubina !== null &&
    inr !== null &&
    sodio !== null &&
    albumina !== null
  ) {
    const { score } = calcularMELD({
      creatinina,
      bilirrubina,
      inr,
      sodio,
      albumina,
      sexoFeminino,
    });
    if (score > 0)
      return { valor: score, data, origem: "calculado", componentesSuspeitos: suspeitos(v) };
  }

  // Sem os 5 componentes: aceita o valor gravado (exame antigo/importado que só
  // trouxe o score). Nunca estimar a partir de componentes parciais.
  const gravado = num(v.MELD);
  if (gravado !== null && gravado > 0) {
    // Score gravado: não recalculamos, mas os componentes presentes ainda podem
    // denunciar digitação errada — sinaliza igual.
    return { valor: gravado, data, origem: "gravado", componentesSuspeitos: suspeitos(v) };
  }
  return null;
}

/**
 * MELD do exame MAIS RECENTE em que ele seja determinável — é o número que o
 * médico vê na tela. NÃO escolhe o MELD mais alto: o critério fala do score
 * vigente, não do pior histórico.
 *
 * Regras (todas vindas da revisão adversarial):
 *  - exige DATA legível (o laudo tem de citar o valor com a data);
 *  - ignora exame etiquetado como pós-transplante (MELD não se aplica ao enxerto);
 *  - desempate no MESMO dia pela coleta mais tardia (LRA da noite não pode
 *    perder para a coleta da manhã);
 *  - marca `dentroDaValidade` pela faixa do próprio MELD (não descarta o exame
 *    vencido — sinaliza, para o laudo exigir conferência humana).
 *
 * @param exames  exames anonimizados (a ordem do array NÃO importa).
 * @param hoje    injetável para teste (default: agora).
 */
export function meldMaisRecenteDeExames(
  exames: ExameAnonimizadoMeld[],
  sexoFeminino: boolean,
  hoje: Date = new Date()
): MeldDeExame | null {
  let melhor: MeldDeExame | null = null;
  let melhorChave: [number, number, number] = [-Infinity, -Infinity, -Infinity];

  (exames || []).forEach((ex, indice) => {
    // Exame pós-Tx nunca ancora MELD (B164/B176: escore de cirrose nativa).
    const tipo = String((ex?.valores as any)?.tipoExame || "").toLowerCase();
    if (tipo === "pos") return;

    const m = meldDoExameAnonimizado(ex, sexoFeminino);
    if (!m) return;
    const tempo = tempoDaData(m.data);
    if (tempo === null) return; // sem data legível não vira critério citável

    const coleta = num(ex?.coletaNumero) ?? 0;
    const chave: [number, number, number] = [tempo, coleta, indice];
    if (maior(chave, melhorChave)) {
      const limite = tempo + validadeDiasMeld(m.valor) * 86400000;
      melhor = { ...m, dentroDaValidade: limite >= hoje.getTime() };
      melhorChave = chave;
    }
  });

  return melhor;
}

/** Compara as chaves de desempate (data → nº da coleta → posição no array). */
function maior(a: [number, number, number], b: [number, number, number]): boolean {
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return a[i] > b[i];
  }
  return false;
}

/** Data ISO (AAAA-MM-DD) ou BR (DD/MM/AAAA) → epoch local; null se ilegível. */
function tempoDaData(raw: string): number | null {
  const s = String(raw || "").trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(+iso[1], +iso[2] - 1, +iso[3]).getTime();
  const br = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (br) return new Date(+br[3], +br[2] - 1, +br[1]).getTime();
  return null;
}

/** Data (ISO ou BR) → DD/MM/AAAA para exibição; devolve o original se ilegível. */
export function formatarDataBRMeld(raw: string): string {
  const s = String(raw || "").trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return s;
}
